import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import ScheduleItemAPI from '../api/scheduleItemApi';

import { normalizeScheduleItemsFilter } from '../utils/woDateFilter';

import type {

  CreateScheduleItemRequest,

  UpdateScheduleItemRequest,

  GetScheduleItemsFilter,

  ConvertToWorkOrderRequest,

} from '../types/scheduleItem.types';



// Query keys

export const scheduleItemKeys = {

  all: ['scheduleItems'] as const,

  byDateRange: (filter: GetScheduleItemsFilter) =>

    [...scheduleItemKeys.all, 'byDateRange', filter] as const,

  byId: (id: number) => [...scheduleItemKeys.all, 'byId', id] as const,

};



// Hook to fetch schedule items by date range

export function useScheduleItems(filter: GetScheduleItemsFilter) {

  const normalizedFilter = normalizeScheduleItemsFilter(filter);



  const isEnabled = Boolean(

    normalizedFilter.startDate && normalizedFilter.endDate,

  );



  return useQuery({

    queryKey: scheduleItemKeys.byDateRange(normalizedFilter),

    queryFn: () => {

      console.log('[useScheduleItems] Fetching with filter:', normalizedFilter);

      return ScheduleItemAPI.getByDateRange(normalizedFilter);

    },

    enabled: isEnabled,

    staleTime: 1 * 60 * 1000,

    refetchOnMount: true,

    refetchOnWindowFocus: true,

  });

}



// Hook to fetch a single schedule item

export function useScheduleItem(scheduleItemId: number) {

  return useQuery({

    queryKey: scheduleItemKeys.byId(scheduleItemId),

    queryFn: () => ScheduleItemAPI.getById(scheduleItemId),

    enabled: scheduleItemId > 0,

  });

}



// Hook to create a schedule item

export function useCreateScheduleItem() {

  const queryClient = useQueryClient();



  return useMutation({

    mutationFn: (request: CreateScheduleItemRequest) => ScheduleItemAPI.create(request),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: scheduleItemKeys.all });

    },

  });

}



// Hook to update a schedule item

export function useUpdateScheduleItem() {

  const queryClient = useQueryClient();



  return useMutation({

    mutationFn: (request: UpdateScheduleItemRequest) => ScheduleItemAPI.update(request),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({

        queryKey: scheduleItemKeys.byId(variables.scheduleItemId),

      });

      queryClient.invalidateQueries({ queryKey: scheduleItemKeys.all });

    },

  });

}



// Hook to delete a schedule item

export function useDeleteScheduleItem() {

  const queryClient = useQueryClient();



  return useMutation({

    mutationFn: (scheduleItemId: number) => ScheduleItemAPI.delete(scheduleItemId),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: scheduleItemKeys.all });

    },

  });

}



// Hook to convert schedule item to work order

export function useConvertToWorkOrder() {

  const queryClient = useQueryClient();



  return useMutation({

    mutationFn: (request: ConvertToWorkOrderRequest) =>

      ScheduleItemAPI.convertToWorkOrder(request),

    onSuccess: (_, variables) => {

      queryClient.invalidateQueries({

        queryKey: scheduleItemKeys.byId(variables.scheduleItemId),

      });

      queryClient.invalidateQueries({ queryKey: scheduleItemKeys.all });

    },

  });

}


